// idea-vault/IdeaVaultPage.tsx
import React, { useState, useEffect } from 'react';
import { Plus, Search, Eye, Star, Video, FileText, MessageSquare, Mic, Sparkles, MoreHorizontal, Edit, Trash2, Clock, Users, TrendingUp, Calendar, Filter, Grid, List } from 'lucide-react';
import { useIdeasStore, Idea } from '../../store/ideasStore';
import { useTeamStore } from '../../store/teamStore';
import { useAuthStore } from '../../store/authStore';
import AddIdeaModal from './AddIdeaModal';
import ViewIdeaModal from './ViewIdeaModal';

const IdeaVaultPage: React.FC = () => {
  const {
    ideas,
    isLoading,
    searchTerm,
    filterCategory,
    filterPriority,
    setSearchTerm,
    setFilterCategory,
    setFilterPriority,
    fetchIdeas,
    deleteIdea
  } = useIdeasStore();

  const { rolePermissions, fetchRolePermissions } = useTeamStore();
  const { user } = useAuthStore();

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [viewingIdea, setViewingIdea] = useState<Idea | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const [showAISuggestions, setShowAISuggestions] = useState(false);

  useEffect(() => {
    fetchIdeas();
    fetchRolePermissions();
  }, [fetchIdeas, fetchRolePermissions]);

  // Permission helper
  const hasPermission = (permission: string) => {
    if (!user?.role) return false;
    if (['Admin', 'Owner'].includes(user.role)) return true;
    return rolePermissions[user.role]?.includes(permission) || false;
  };


  // Helper function to format dates to just dd-mm-yyyy
  const formatDateOnly = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return '';

    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      if (isNaN(date.getTime())) return '';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const priorities = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <Star className="w-4 h-4 fill-red-500 text-red-500" />;
      case 'medium': return <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />;
      case 'low': return <Star className="w-4 h-4 fill-green-500 text-green-500" />;
      default: return <Star className="w-4 h-4 text-gray-400" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'blog': return <FileText className="w-4 h-4" />;
      case 'social': return <MessageSquare className="w-4 h-4" />;
      case 'podcast': return <Mic className="w-4 h-4" />;
      default: return <Sparkles className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'video': return 'bg-red-100 text-red-700';
      case 'blog': return 'bg-primary-100 text-primary-700';
      case 'social': return 'bg-green-100 text-green-700';
      case 'podcast': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredIdeas = ideas.filter(idea => {
    const matchesSearch = !searchTerm ||
      idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = filterCategory === 'all' || idea.category === filterCategory;
    const matchesPriority = filterPriority === 'all' || idea.priority === filterPriority;

    return matchesSearch && matchesCategory && matchesPriority;
  });

  const highPriorityIdeas = ideas.filter(idea => idea.priority === 'high');
  const totalEffort = ideas.reduce((sum, idea) => sum + (idea.estimatedEffort || 0), 0);
  const averageEffort = ideas.length > 0 ? Math.round(totalEffort / ideas.length * 10) / 10 : 0;

  const handleEditIdea = (idea: Idea) => {
    setSelectedIdea(idea);
    setShowAddModal(true);
  };

  const handleViewIdea = (idea: Idea) => {
    setViewingIdea(idea);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setSelectedIdea(null);
  };

  // Check view permission
  if (!hasPermission('view_ideas')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view the Idea Vault.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Idea Vault</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Centralized repository for your content ideas</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Ideas</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white text-center break-all">
              {ideas.length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">High Priority</div>
            <div className="text-xl font-bold text-red-600 text-center break-all">
              {highPriorityIdeas.length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Avg Effort</div>
            <div className="text-xl font-bold text-green-600 text-center break-all">
              {averageEffort}h
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters & Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search ideas, tags, or descriptions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
              />
            </div>
            <div className="flex gap-2 min-w-0">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors min-w-[150px] max-w-full"
              >
                <option value="all">All Categories</option>
                <option value="video">Video</option>
                <option value="blog">Blog</option>
                <option value="social">Social</option>
                <option value="podcast">Podcast</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors min-w-[150px] max-w-full"
              >
                {priorities.map(priority => (
                  <option key={priority.value} value={priority.value}>{priority.label}</option>
                ))}
              </select>
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors flex-shrink-0">
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">More Filters</span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'cards' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <Grid className="w-4 h-4" />
                Grid
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
            </div>
            {hasPermission('create_idea') && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Plus className="w-4 h-4" />
                New Idea
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Display */}
      {filteredIdeas.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No ideas found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {ideas.length === 0
              ? "You don't have any ideas yet. Create your first one to start building your content vault."
              : "Try adjusting your search or filter to find what you're looking for."}
          </p>
          {hasPermission('create_idea') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Create Idea
            </button>
          )}
        </div>
      ) : (
        <>
          {viewMode === 'cards' ? (
            /* Cards View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredIdeas.map(idea => (
                <div key={idea.id} className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden flex flex-col">
                  <div className="p-6 flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
                          {idea.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                          {idea.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(idea.priority)}`}>
                          {getPriorityIcon(idea.priority)}
                        </span>
                        <button className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                          <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(idea.category)}`}>
                          {getCategoryIcon(idea.category)}
                          <span className="ml-1 capitalize">{idea.category}</span>
                        </span>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                            <Clock className="w-3 h-3" />
                            <span>{idea.estimatedEffort}h</span>
                          </div>
                        </div>
                      </div>

                      {idea.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {idea.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                              #{tag}
                            </span>
                          ))}
                          {idea.tags.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                              +{idea.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      {idea.targetAudience && idea.targetAudience.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                          <Users className="w-3 h-3" />
                          <span>{idea.targetAudience.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {hasPermission('edit_idea') && (
                          <button
                            onClick={() => handleEditIdea(idea)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleViewIdea(idea)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {hasPermission('delete_idea') && (
                          <button
                            onClick={() => deleteIdea(idea.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDateOnly(idea.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Idea & Description</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider max-w-[120px]">Priority</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Effort</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tags</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredIdeas.map(idea => (
                      <tr key={idea.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm mr-3 ${getCategoryColor(idea.category)}`}>
                              {getCategoryIcon(idea.category)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white line-clamp-1">{idea.title}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{idea.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(idea.category)}`}>
                            {getCategoryIcon(idea.category)}
                            <span className="ml-1 capitalize">{idea.category}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 max-w-[120px]">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getPriorityColor(idea.priority)}`}>
                            {getPriorityIcon(idea.priority)}
                            <span className="ml-1 capitalize">{idea.priority}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{idea.estimatedEffort}h</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1 max-w-32">
                            {idea.tags.slice(0, 2).map(tag => (
                              <span key={tag} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                                #{tag}
                              </span>
                            ))}
                            {idea.tags.length > 2 && (
                              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                                +{idea.tags.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDateOnly(idea.createdAt)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {hasPermission('edit_idea') && (
                              <button
                                onClick={() => handleEditIdea(idea)}
                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleViewIdea(idea)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {hasPermission('delete_idea') && (
                              <button
                                onClick={() => deleteIdea(idea.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Floating Action Button for AI Suggestions - Round */}
      <button
        onClick={() => setShowAISuggestions(true)}
        className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center hover:scale-110 z-40"
        aria-label="AI Suggestions"
      >
        <Sparkles className="w-5 h-5" />
      </button>

      {/* AI Suggestions Floating Card - Small card above FAB */}
      {showAISuggestions && (
        <div className="fixed bottom-24 right-8 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 border border-gray-200 dark:border-gray-700">
          {/* Card Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">AI Suggestions</h3>
            </div>
            <button
              onClick={() => setShowAISuggestions(false)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Card Content */}
          <div className="p-4 max-h-96 overflow-y-auto">
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg border border-primary-100 hover:shadow-md transition-shadow cursor-pointer">
                <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-900 dark:text-white">"React vs Vue comparison"</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">Trending in tech</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg border border-primary-100 hover:shadow-md transition-shadow cursor-pointer">
                <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-900 dark:text-white">"Home office setup tour"</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">Popular lifestyle</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg border border-primary-100 hover:shadow-md transition-shadow cursor-pointer">
                <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-900 dark:text-white">"Productivity apps review"</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">High engagement</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-gradient-to-r from-primary-50 to-purple-50 rounded-lg border border-primary-100 hover:shadow-md transition-shadow cursor-pointer">
                <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-gray-900 dark:text-white">"AI tools for creators"</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">Emerging trend</p>
                </div>
              </div>
            </div>
          </div>

          {/* Card Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button className="w-full px-3 py-2 bg-gradient-to-r from-primary-600 to-indigo-600 text-white text-sm rounded-lg hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium">
              Generate More
            </button>
          </div>
        </div>
      )}

      {/* Add Idea Modal */}
      {showAddModal && (
        <AddIdeaModal
          isOpen={showAddModal}
          onClose={handleCloseModal}
          idea={selectedIdea}
        />
      )}

      {/* View Idea Modal */}
      {viewingIdea && (
        <ViewIdeaModal
          isOpen={!!viewingIdea}
          onClose={() => setViewingIdea(null)}
          idea={viewingIdea}
        />
      )}
    </div>
  );
};

export default IdeaVaultPage;
